import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Polyline } from 'react-google-maps';
import GoogleMap from "react-google-maps/lib/components/GoogleMap";
import Marker from 'react-google-maps/lib/components/Marker';
import { useDispatch, useSelector } from "react-redux";
import { iDeviceDetails, iDevicePing, iFullStoreState, iLocation } from "../../shared/interfaces";
import { Actions as MapActions } from "../../stores/reducers/gmap-reducers";
import { ReplaySpeed, setReplayPlaying } from '../../stores/reducers/replay/AC';
import { removeSelectedPoint, setSelectedPoint } from '../../stores/reducers/selectedPoint/AC';
import { useReplayControl } from '../gmap/ReplyContext';
import MapProgressSlider from '../GoogleMap/MapProgressSlider/MapProgressSlider';

type IReplyProps = {
    location: Array<iLocation | google.maps.LatLng>;
    deviceId: string;
    polylineOptions: Object;
    routeDetails: Array<{
        color: string;
        points: Array<{ lat: number; lng: number; pointId: string; isLast?: boolean }>;
        polylinePath?: Array<google.maps.LatLng | google.maps.LatLngLiteral>;
    }>;
    firstPoint: any;
    onReplayPointUpdate: (point: iDevicePing) => void;
    onReplayStateChange?: (isPlaying: boolean) => void;
}

export const Reply = (props: IReplyProps) => {
    const dispatch = useDispatch();

    const replaySpeed = useSelector<iFullStoreState, ReplaySpeed>(state => state.replay?.speed || ReplaySpeed.NORMAL);

    const replaySpeedRef = useRef(replaySpeed);
    useEffect(() => {
        replaySpeedRef.current = replaySpeed;
    }, [replaySpeed]);

    const pathInterval = useRef<NodeJS.Timeout>();
    const { location, deviceId, polylineOptions, routeDetails, firstPoint } = props;
    const [currentPosition, setCurrentPosition] = useState(location[0]);
    const [locations, setLocations] = useState([]);
    const [startPlayBtnAttr, setStartPlayBtnAttr] = useState(false);
    const [currentPositionCounter, setCurrentPositionCounter] = useState(0);
    const [currentRouteNumber, setCurrentRouteNumber] = useState(1);
    const replayPath = useSelector<iFullStoreState, boolean>((s) => s.gmap.replayPath);
    const [shouldMoveDevice, setShouldMoveDevice] = useState(true);
    const [currentDeviceColor, setCurrentDeviceColor] = useState('');
    const currentDevice = useSelector<iFullStoreState, iDeviceDetails>(
        (s) => s.devicesData.devicesDetails.getIn([`${deviceId}`]) as iDeviceDetails
    );
    const [pointsArrayMultipleState, setPointsArrayMultipleState] = useState<Array<Array<google.maps.LatLng | google.maps.LatLngLiteral>>>([]);

    const tripsPointsData = useSelector<iFullStoreState, any>(
        (state) => state.devicesTripsPoints.getIn([deviceId, 'tripsPoints', firstPoint?.tripId])
    );

    const [activeRoute, updateActiveRoute] = useState(routeDetails[0]['points']);
    const activeRouteRef = useRef(activeRoute);

    const setActiveRoute = state => {
        updateActiveRoute(state);
        activeRouteRef.current = state;
    }
    const { registerReplayControl, unregisterReplayControl, startReplay, pauseReplay, resetReplay } = useReplayControl();

    const navigateToPoint = useCallback((pointIndex: number) => {
        if (!activeRouteRef.current || pointIndex < 0 || pointIndex >= activeRouteRef.current.length) {
            return;
        }

        // Stop any active replay
        if (pathInterval.current) {
            clearInterval(pathInterval.current);
            pathInterval.current = null; // Set to null to prevent restart
            setStartPlayBtnAttr(false);
            dispatch(setReplayPlaying(deviceId, false));
        }

        // Set the new position
        setActiveCountNumber(pointIndex);
        setCurrentPosition(activeRouteRef.current[pointIndex]);

        // Get the full point data using pointId from the current route point
        const currentRoutePoint = activeRouteRef.current[pointIndex];
        const fullPointData = currentRoutePoint?.pointId && tripsPointsData ?
            tripsPointsData.get(currentRoutePoint.pointId) : null;

        // Update the modal with the new point data
        props.onReplayPointUpdate(fullPointData || activeRouteRef.current[pointIndex]);

        // Update the path visualization (show path up to current point)
        setLocations(activeRouteRef.current.slice(0, pointIndex + 1));

        if (fullPointData) {
            // For manual navigation through prev/next buttons, still don't show info window
            // unless user explicitly clicked on the point
            dispatch(setSelectedPoint(deviceId, fullPointData.tripId, fullPointData.pointId, fullPointData.coordinates.location, false));
        }
    }, [activeRouteRef, pathInterval, deviceId, dispatch, tripsPointsData, props.onReplayPointUpdate]);

    const navigateToNextPoint = useCallback(() => {
        const currentIndex = activeCountNumber.current;
        const nextIndex = currentIndex + 1;

        if (nextIndex < activeRouteRef.current.length) {
            navigateToPoint(nextIndex);
        }
    }, [navigateToPoint]);

    const navigateToPrevPoint = useCallback(() => {
        const currentIndex = activeCountNumber.current;
        const prevIndex = currentIndex - 1;

        if (prevIndex >= 0) {
            navigateToPoint(prevIndex);
        }
    }, [navigateToPoint]);

    const getCurrentPointInfo = useCallback(() => {
        return {
            currentIndex: activeCountNumber.current,
            totalPoints: activeRouteRef.current?.length || 0,
            hasNext: activeCountNumber.current < (activeRouteRef.current?.length || 0) - 1,
            hasPrev: activeCountNumber.current > 0
        };
    }, []);

    const activeCountNumber = useRef(0);

    const setActiveCountNumber = state => {
        setCurrentPositionCounter(currentPositionCounter);
        activeCountNumber.current = state;
    }

    const currentRouteIndexRef = useRef(currentRouteNumber);

    const setCurrentRouteIndexRef = state => {
        setCurrentRouteNumber(state);
        currentRouteIndexRef.current = state;
    }

    const currentDeviceColorRef = useRef(currentDeviceColor);

    const setCurrentDeviceColorRef = state => {
        setCurrentDeviceColor(state);
        currentDeviceColorRef.current = state;
    }

    const toggleReplayPath = () => dispatch(MapActions.TOGGLE_REPLAY_PATH());

    const resetToStart = () => {
        setActiveRoute(routeDetails[0]['points'])
        setPointsArrayMultipleState([]);
        setActiveCountNumber(0);
        setCurrentPosition(routeDetails[0]['points'][0]);
        setLocations([]);
    }

    let changeDevicePosition = (i) => {
        let finishedArrayIndex = 0;
        // Always use the latest speed value from the ref
        const currentSpeed = replaySpeedRef.current;
        // Calculate interval: 0.5x=600ms, 1x=300ms, 1.5x=200ms, 2x=150ms
        const intervalMs = Math.max(100, 300 / currentSpeed);

        pathInterval.current = setInterval(() => {
            if (!activeRouteRef.current || !activeRouteRef.current[activeCountNumber.current]) {
                clearInterval(pathInterval.current);
                return;
            }
            const isLastPoint = activeRouteRef.current[activeCountNumber.current]['isLast'];
            const shouldPlayNextRoute = isLastPoint && routeDetails.length > 1 && currentRouteIndexRef.current < routeDetails.length;
            const shouldTakeNextPoint = i < activeRouteRef.current.length;
            if (shouldPlayNextRoute) {
                // Defensive check: ensure the route exists before accessing it
                const nextRouteIndex = currentRouteIndexRef.current;
                if (nextRouteIndex < routeDetails.length && routeDetails[nextRouteIndex] && routeDetails[nextRouteIndex]['points']) {
                    setActiveRoute(routeDetails[nextRouteIndex]['points']);
                    setCurrentRouteIndexRef(currentRouteIndexRef.current + 1);
                    setPointsArrayMultipleState(prevState => [
                        ...prevState,
                        routeDetails[finishedArrayIndex]?.polylinePath || routeDetails[finishedArrayIndex]['points'],
                    ])
                    finishedArrayIndex++;
                    setActiveCountNumber(0);
                    setLocations([routeDetails[nextRouteIndex]['points'][0]]); // Reset locations for new route
                    i = 0;
                } else {
                    // If no valid next route, stop the replay
                    clearInterval(pathInterval.current);
                    setActiveCountNumber(0);
                    toggleReplayPath();
                    return;
                }
            }
            else if (shouldTakeNextPoint) {
                if (i === 0) {
                    // Add bounds check for finishedArrayIndex
                    if (finishedArrayIndex < routeDetails.length && routeDetails[finishedArrayIndex]) {
                        setCurrentDeviceColorRef(routeDetails[finishedArrayIndex]['color']);
                    }
                }
                setActiveCountNumber(i);
                setCurrentPosition(activeRouteRef.current[i]);

                // Get the full point data using pointId from the current route point
                const currentRoutePoint = activeRouteRef.current[i];
                const fullPointData = currentRoutePoint?.pointId && tripsPointsData ?
                    tripsPointsData.get(currentRoutePoint.pointId) : null;

                props.onReplayPointUpdate(fullPointData || activeRouteRef.current[i]);
                setLocations(activeRouteRef.current.slice(0, i + 1));

                // Update selectedPoint with isClicked=false for replay points
                // This ensures InfoSelectedPoint knows this is an automated navigation, not user click
                if (fullPointData) {
                    dispatch(setSelectedPoint(deviceId, fullPointData.tripId, fullPointData.pointId, fullPointData.coordinates.location, false));
                }

                i++;
            }
            else {
                clearInterval(pathInterval.current);
                pathInterval.current = null; // Set to null to prevent restart
                setActiveCountNumber(0);
                setStartPlayBtnAttr(false); // Set to false when trip finishes
                dispatch(setReplayPlaying(deviceId, false));
                // Notify modal that replay has stopped
                props.onReplayStateChange?.(false);
                toggleReplayPath();
            }
        }, intervalMs);
    }

    const pausePlay = useCallback(() => {
        if (pathInterval.current) {
            clearInterval(pathInterval.current);
            pathInterval.current = null; // Set to null to prevent restart
        }
        setStartPlayBtnAttr(false); // Always set to false when pausing
        // Update Redux state
        dispatch(setReplayPlaying(deviceId, false));
        // Notify modal about state change
        props.onReplayStateChange?.(false);
    }, [deviceId, dispatch, props.onReplayStateChange]);

    const startPlay = useCallback(() => {
        const isFirstPoint = activeCountNumber.current === 0;

        // Only dispatch setSelectedPoint for the initial play, not for speed changes
        // For replay, set isClicked to false to ensure InfoSelectedPoint doesn't show unexpectedly
        if (isFirstPoint && firstPoint?.coordinates?.location) {
            dispatch(setSelectedPoint(firstPoint.device, firstPoint.tripId, firstPoint.pointId, firstPoint.coordinates.location, false));
        }

        isFirstPoint ? toggleReplayPath() : setCurrentPositionCounter(activeCountNumber.current);
        changeDevicePosition(activeCountNumber.current);
        setStartPlayBtnAttr(true);
        setShouldMoveDevice(true); // Ensure device movement is enabled
        dispatch(setReplayPlaying(deviceId, true));
        props.onReplayStateChange?.(true);
    }, [firstPoint?.device, firstPoint?.tripId, firstPoint?.pointId, firstPoint?.coordinates?.location, dispatch, deviceId, props.onReplayStateChange]);

    const resetPath = useCallback(() => {
        // Clear interval first and ensure it stays cleared
        if (pathInterval.current) {
            clearInterval(pathInterval.current);
            pathInterval.current = null; // Set to null to prevent restart
        }

        dispatch(removeSelectedPoint());
        resetToStart();
        setCurrentRouteIndexRef(1);
        setStartPlayBtnAttr(false);
        dispatch(setReplayPlaying(deviceId, false));

        // Notify modal about state change
        props.onReplayStateChange?.(false);
    }, [dispatch, deviceId, props.onReplayStateChange]);

    useEffect(() => {
        const isNotLastPoint = currentPositionCounter < activeRoute.length;
        if (isNotLastPoint) {
            setLocations([...activeRoute.slice(0, activeCountNumber.current), currentPosition])
        }
    }, [activeCountNumber.current])

    useEffect(() => {
        return () => activeCountNumber.current > 0 && toggleReplayPath()
    }, [])

    // Real-time speed adjustment during replay
    useEffect(() => {
        // Only restart if there's an active interval AND the play button is pressed (actively playing)
        // Also check that pathInterval.current is not null (prevents restart after reset)
        if (pathInterval.current && startPlayBtnAttr && pathInterval.current !== null) {
            clearInterval(pathInterval.current);
            // Small delay to ensure cleanup, then restart
            setTimeout(() => {
                // Double-check that we should still be playing after the delay
                if (startPlayBtnAttr) {
                    changeDevicePosition(activeCountNumber.current);
                }
            }, 50);
        }
    }, [replaySpeed]);

    const mutatedPolylineOptions = { ...polylineOptions, strokeColor: currentDeviceColor }

    useEffect(() => {
        const controls = {
            startPlay,
            pausePlay,
            resetPath,
            navigateToNextPoint,
            navigateToPrevPoint,
            getCurrentPointInfo,
        };
        registerReplayControl(deviceId, controls);

        // Cleanup on unmount
        return () => {
            unregisterReplayControl(deviceId);
        };
    }, [deviceId, registerReplayControl, unregisterReplayControl, startPlay, pausePlay, resetPath, navigateToNextPoint, navigateToPrevPoint, getCurrentPointInfo]);


    return (
        <>
            {/* <ReplyComponent
            startPlayHandler={startPlay}
            resetPathHandler={resetPath}
            isDisabledResetBtn={resetPathBtnAttr}
            isDisabledStartBtn={startPlayBtnAttr}
            pausePlayHandler={pausePlay}
            shouldMoveDevice={shouldMoveDevice}
        /> */}

            <MapProgressSlider />
            <Marker
                position={currentPosition}
                title={'Car'}
            />
            {replayPath && <Polyline path={locations} options={mutatedPolylineOptions} />}
            {
                pointsArrayMultipleState.map((item, index) => <Polyline path={item} options={polylineOptions} key={index} />)
            }
        </>
    );
}
